import { useEffect, useReducer, useRef } from "react";
import { handleError } from "../../common/ErrorHandling";
import {
    createDraftProject,
    deleteDraftProject,
    renameProject,
    fetchSession,
    saveIntakeSurvey,
    setAnalysisKind,
    postMessage,
    startConversation,
    acknowledgeUploads,
    setIntakeMode,
    submitProject,
    uploadDocument,
    deleteDocument,
    ExtractionStatus,
    IntakeMode,
    IntakeSession,
    IntakeSessionMessage,
    LogicalSection,
    RequirementGroup,
    SurveyResponse,
} from "./GuidedIntakeApi";
import { AnalysisKind, hasAllowedExtension, ProjectType } from "../../types/ProjectTypes";
import { useChatDemoFlag } from "../demo/demoFlags";
import {
    DEMO_ACK_MS,
    DEMO_CONVERSATION_OPENER,
    DEMO_DOCUMENTS,
    DEMO_EXCHANGES,
    DEMO_SUGGESTED_NAME,
    DEMO_UPLOAD_READ_BACK,
} from "./intakeDemoScript";
import { ReactComponent as ContractAnalysisIcon } from "../../assets/icons/svg/contract-analysis-gradient.svg";
import { ReactComponent as RfxAnalysisIcon } from "../../assets/icons/svg/rfc_logo.svg";
import { ReactComponent as TalkThroughIcon } from "../../assets/icons/svg/message01-gradient.svg";
import { ReactComponent as SurveyIcon } from "../../assets/icons/svg/list-gradient.svg";

// ── Wizard copy — the prompts, choices, and greetings each step shows ───────

// Wrap wizard copy as a bot turn so it renders like any other chat bubble.
const botMessage = (id: string, content: string): ChatMessage => ({
    id,
    kind: "text",
    role: "system",
    content,
});

// —— 1. Project type ————

export const TYPE_CHOICE_MESSAGE = botMessage(
    "type-choice",
    "Hi! What would you like me to help you with?"
);

export const TYPES: ProjectType[] = [ProjectType.DEAL, ProjectType.RFX];

export const TYPE_CHOICES: Record<
    ProjectType,
    { title: string; subtitle: string; Icon: typeof ContractAnalysisIcon }
> = {
    [ProjectType.DEAL]: {
        title: "Analyze contracts",
        subtitle: "Upload single contracts to review and analyze.",
        Icon: ContractAnalysisIcon,
    },
    [ProjectType.RFX]: {
        title: "Compare proposals",
        subtitle: "Upload proposals to compare and analyze.",
        Icon: RfxAnalysisIcon,
    },
};

// —— 2. Analysis kind ————

export const KIND_CHOICE_MESSAGE = botMessage(
    "kind-choice",
    "What kind of analysis do you need?"
);

/** The analysis kinds offered per family, with their card copy. */
export const KINDS_BY_TYPE: Record<
    ProjectType,
    { kind: AnalysisKind; title: string; subtitle: string }[]
> = {
    [ProjectType.DEAL]: [
        {
            kind: AnalysisKind.DEAL_PLAYBOOK,
            title: "Full Negotiation Playbook",
            subtitle: "Leverage, tactics, and market context for your whole negotiation.",
        },
        {
            kind: AnalysisKind.COMMERCIAL_ASSESSMENT,
            title: "Commercial Assessment",
            subtitle: "A read on pricing and commercial terms from your order form or spend.",
        },
        {
            kind: AnalysisKind.RATE_CARD_ASSESSMENT,
            title: "Rate Card Assessment",
            subtitle: "A benchmark of the rates on a vendor's rate card.",
        },
        {
            kind: AnalysisKind.MSA_ASSESSMENT,
            title: "MSA Assessment",
            subtitle: "A risk and terms review of your master services agreement.",
        },
    ],
    [ProjectType.RFX]: [
        {
            kind: AnalysisKind.RFI,
            title: "RFI",
            subtitle: "Structure what you're sourcing and what to ask vendors.",
        },
        {
            kind: AnalysisKind.RFP,
            title: "RFP",
            subtitle: "Compare vendor proposals against your requirements.",
        },
        {
            kind: AnalysisKind.RFQ,
            title: "RFQ",
            subtitle: "Compare vendor quotes and pricing against your requirements.",
        },
    ],
};

/** Display title of an analysis kind — the card title, echoed by the Context checklist. */
export const kindTitle = (kind: AnalysisKind): string => {
    for (const kinds of Object.values(KINDS_BY_TYPE)) {
        const found = kinds.find((k) => k.kind === kind);
        if (found) return found.title;
    }
    return kind;
};

// —— 3. Upload ————

/** The Upload step's opening message — names the documents worth uploading per kind. */
export const UPLOAD_GREETINGS: Record<AnalysisKind, ChatMessage> = {
    [AnalysisKind.DEAL_PLAYBOOK]: botMessage(
        "greeting",
        "Drop in the contract and anything related: the order form or quote, the MSA, " +
            "and any pricing schedules, amendments, or SOWs you have. " +
            "I'll read them and take it from there."
    ),
    [AnalysisKind.COMMERCIAL_ASSESSMENT]: botMessage(
        "greeting",
        "Drop in the order form or SOW along with any related document. " +
            "I'll read it and take it from there."
    ),
    [AnalysisKind.RATE_CARD_ASSESSMENT]: botMessage(
        "greeting",
        "Drop in the rate card along with any related pricing schedules or fee exhibits. " +
            "I'll read it and take it from there."
    ),
    [AnalysisKind.MSA_ASSESSMENT]: botMessage(
        "greeting",
        "Drop in the MSA along with any amendments or addenda. " +
            "I'll read it and take it from there."
    ),
    [AnalysisKind.RFI]: botMessage(
        "greeting",
        "Drop in your requirements or specifications if you have them. " +
            "I'll read them and take it from there."
    ),
    [AnalysisKind.RFP]: botMessage(
        "greeting",
        "Drop in the RFP or requirements doc and each vendor's proposal, plus any " +
            "pricing responses, scorecards, or Q&A you have. " +
            "I'll read them and take it from there."
    ),
    [AnalysisKind.RFQ]: botMessage(
        "greeting",
        "Drop in the RFQ or requirements doc and each vendor's quote, plus any " +
            "pricing responses or scorecards you have. " +
            "I'll read them and take it from there."
    ),
};

// —— 4. Mode (chat vs survey) ————

export const MODE_CHOICE_MESSAGE = botMessage(
    "mode-choice",
    "How would you like to give me the context not present in the documents?"
);

export interface ModeOption {
    mode: IntakeMode;
    Icon: typeof TalkThroughIcon;
    title: string;
    subtitle: string;
    tag: string;
}

export const MODE_OPTIONS: ModeOption[] = [
    {
        mode: IntakeMode.CHAT,
        Icon: TalkThroughIcon,
        title: "Talk it through",
        subtitle:
            "Answer in your own words. I'll ask follow-ups and dig into the specifics.",
        tag: "Adaptive",
    },
    {
        mode: IntakeMode.SURVEY,
        Icon: SurveyIcon,
        title: "Survey",
        subtitle: "Fill out a fixed set of questions in a form.",
        tag: "Fixed",
    },
];

// ── Model — the shape of the data the UI renders ────────────────────────────

export enum DocState {
    Queued = "queued",     // doc queued, but not sent yet, we only upload/parse 4 at a time
    Parsing = "parsing",   // doc being uploaded and parsed
    Ready = "ready",       // doc uploaded and potentially parsed
    Failed = "failed",     // doc failed uploading because bad type (no failure on parsing)
}

export type Document = {
    id: string;          // local id
    documentId?: number; // backend ILProjectDocument id
    label: string;       // filename
    state: DocState;     // state
    size?: string;       // size as a formatted string
    types?: string[];    // document types found in the file
    error?: string;      // failure reason, shown on the row when state is Failed
};

/**
 * One row of the Topics list in the Context box.
 */
export type Topic = {
    id: string;
    label: string;
    hint: string;
    status: "pending" | "done";
    /**
     * Shown in place of the hint when set. Only the wizard's own rows use it
     * (echoing the chosen project type / analysis kind) — context topics
     * always show their hint, never a recap of the user's answers.
     */
    summary?: string;
};

/**
 * One chat message — a `text` turn from the user or the `system` (agent).
 * `typing` flags a system placeholder bubble while the reply streams in.
 * `interjection` flags an upload read-back — rendered as a note rather than
 * an agent bubble, and not part of the conversation with the agent.
 */
export type ChatMessage = {
    id: string;
    kind: "text";
    role: "user" | "system";
    content: string;
    typing?: boolean;
    interjection?: boolean;
};

/**
 * The intake's position in its sequence — type → kind → upload → mode →
 * (mode-dependent). A single enum instead of a set of booleans:
 *  - `Loading`  — a resume or create is in flight; the view shows a spinner.
 *  - `Type`     — choosing the family (Deal vs RFP analysis; the opening cards).
 *  - `Kind`     — choosing the analysis kind within the family. Picking one
 *                 mints the draft (or persists a change on revisit).
 *  - `Upload`   — kind picked; uploading documents.
 *  - `Mode`     — uploads confirmed; choosing how to answer the rest (adaptive/survey).
 *  - `Adaptive` — free-form chat.
 *  - `Survey`   — the guided-questions survey form.
 * Upload → Mode is an explicit user action: the Continue button — never
 * automatic on upload. Documents are optional; Continue is always available.
 * `sending`/`submitting` are orthogonal async flags, not sequence steps.
 */
export enum IntakePhase {
    Loading = "loading",
    Type = "type",
    Kind = "kind",
    Upload = "upload",
    Mode = "mode",
    Adaptive = "adaptive",
    Survey = "survey",
}

export interface GuidedIntakeState {
    /** The draft's id, or null until a type is picked (which mints the draft). */
    projectId: number | null;
    /** The project name shown in the header, or null while unset. */
    projectName: string | null;
    /** The last name written to the server — rename only fires on a diff. */
    persistedName: string;
    /** True once the user edited the name; suggestions never override it. */
    nameEdited: boolean;
    /** The chosen project type (family), or null on the opening step. */
    projectType: ProjectType | null;
    /** The analysis kind picked on the Kind step, or null before it. */
    analysisKind: AnalysisKind | null;
    /** The kind's document requirements; empty until the kind is picked. */
    documentGroups: RequirementGroup[];
    /** The chosen answering mode, or null until the user picks one (Mode step). */
    intakeMode: IntakeMode | null;
    /** The wizard step currently shown (see IntakePhase). */
    phase: IntakePhase;
    /** The chat transcript, oldest first — rendered on the adaptive step. */
    messages: ChatMessage[];
    /** Every attached document row, including queued/parsing/failed ones. */
    documents: Document[];
    /** The context-topics checklist, with coverage updated after each chat turn. */
    topics: Topic[];
    /** Other document types worth uploading — rendered as a tooltip hint. */
    suggestedDocumentTypes: string[];
    /** Survey mode: schema + answers, kept current per edit (see updateSurvey). */
    survey: SurveyResponse | null;
    /** True while an agent turn (chat reply, opener, read-back) is in flight. */
    sending: boolean;
    /** True while the draft submit is in flight. */
    submitting: boolean;
    /** In-progress upload batch, shown by the Documents title. Null when idle. */
    uploadProgress: { done: number; total: number } | null;
    /** Latest whole-set read-back, shown below the drop area. Null before any upload. */
    uploadReadBack: string | null;
    /** True while the whole-set read is in flight — shows typing dots below the drop area. */
    ackPending: boolean;
}

/**
 * The label shown for a raw extraction document nature, resolved per project type.
 * The extractor emits project-agnostic natures (e.g. "Order form / quote"); only
 * the priced-commercial-doc nature is type-specific — it reads "Order Form" for a
 * deal and "Quote" for an RFx. Every other nature renders as-is. Matching an
 * upload to a checklist row stays raw-nature vs raw-nature (this is display only).
 */
export function docTypeLabel(rawType: string, projectType: ProjectType | null): string {
    if (rawType.trim().toLowerCase() === "order form / quote") {
        return projectType === ProjectType.RFX ? "Quote" : "Order Form";
    }
    return rawType;
}

/**
 * The checklist row for an unmet requirement group: an OR of the document
 * types that satisfy it, in the same per-family labels the pills use (e.g.
 * "Order Form / SOW"). What information can substitute in conversation is the
 * Context checklist's job, not this label's.
 */
export const requirementGroupLabel = (
    group: RequirementGroup,
    projectType: ProjectType | null
): string => group.documentTypes.map((t) => docTypeLabel(t, projectType)).join(" / ");

// ── Phase helpers ────────────────────────────────────────────────────────────

/** The phase a chosen answering mode lands us in. */
const phaseForMode = (mode: IntakeMode): IntakePhase =>
    mode === IntakeMode.SURVEY ? IntakePhase.Survey : IntakePhase.Adaptive;

// ── Selectors — pure projections of the state, computed where they're used ──

/** Whether any document upload is still in flight (queued or being read). */
export const isUploading = (s: GuidedIntakeState): boolean =>
    s.documents.some((d) => d.state === DocState.Queued || d.state === DocState.Parsing);

/**
 * Whether the draft is ready to submit: a type and a mode are chosen (the phase
 * is past Mode) and no upload is still in flight — submitting then would race
 * the analysis against documents that haven't landed yet. Documents are not
 * required (missing requirement groups warn on submit, they never block).
 */
export const canSubmit = (s: GuidedIntakeState): boolean =>
    s.projectType != null &&
    (s.phase === IntakePhase.Adaptive || s.phase === IntakePhase.Survey) &&
    !isUploading(s);

/**
 * The requirement groups not yet satisfied by an upload (any of the group's
 * document types detected) or by conversation (any of its topics covered).
 * Document-type matching is raw nature vs raw nature, case-insensitive.
 */
export function unsatisfiedGroups(
    groups: RequirementGroup[],
    documents: Document[],
    topics: Topic[]
): RequirementGroup[] {
    const detected = new Set(
        documents.flatMap((d) => d.types ?? []).map((t) => t.trim().toLowerCase())
    );
    const covered = new Set(topics.filter((t) => t.status === "done").map((t) => t.id));
    return groups.filter(
        (g) =>
            !g.documentTypes.some((t) => detected.has(t.trim().toLowerCase())) &&
            !(g.topics ?? []).some((id) => covered.has(id))
    );
}

/** The kind's document requirements still unmet — what the submit warning lists. */
export const unmetRequirementGroups = (s: GuidedIntakeState): RequirementGroup[] =>
    unsatisfiedGroups(s.documentGroups, s.documents, s.topics);

// ── State ───────────────────────────────────────────────────────────────

const initialState = (projectId?: number): GuidedIntakeState => ({
    projectId: null,
    projectName: null,
    persistedName: "",
    nameEdited: false,
    projectType: null,
    analysisKind: null,
    documentGroups: [],
    intakeMode: null,
    phase: projectId == null ? IntakePhase.Type : IntakePhase.Loading,
    messages: [],
    documents: [],
    topics: [],
    suggestedDocumentTypes: [],
    survey: null,
    sending: false,
    submitting: false,
    uploadProgress: null,
    uploadReadBack: null,
    ackPending: false,
});

// One shallow-merge action covers every field write; the messages and documents
// lists keep dedicated surgery actions (append / update-by-id / remove-by-id).
type Action =
    | { type: "PATCH"; patch: Partial<GuidedIntakeState> }
    | { type: "ADD_MESSAGE"; message: ChatMessage }
    | { type: "REPLACE_MESSAGE"; id: string; message: ChatMessage }
    | { type: "ADD_DOC"; doc: Document }
    | { type: "SET_DOC"; id: string; patch: Partial<Document> }
    | { type: "REMOVE_DOC"; id: string };

function reducer(state: GuidedIntakeState, action: Action): GuidedIntakeState {
    switch (action.type) {
        case "PATCH":
            return { ...state, ...action.patch };
        case "ADD_MESSAGE":
            return { ...state, messages: [...state.messages, action.message] };
        case "REPLACE_MESSAGE":
            return {
                ...state,
                messages: state.messages.map((m) => (m.id === action.id ? action.message : m)),
            };
        case "ADD_DOC":
            // No phase change: leaving the Upload step is the user's call (the
            // Continue button), not a side effect of attaching a document.
            // Newest first, so a fresh batch is visible loading at the top of
            // a long list.
            return { ...state, documents: [action.doc, ...state.documents] };
        case "SET_DOC":
            return {
                ...state,
                documents: state.documents.map((d) =>
                    d.id === action.id ? { ...d, ...action.patch } : d
                ),
            };
        case "REMOVE_DOC": {
            // Keep the current phase even when the last document is removed — the
            // user has already moved past uploading and can add more later; bouncing
            // back to the Upload step would be confusing.
            const documents = state.documents.filter((d) => d.id !== action.id);
            return { ...state, documents };
        }
    }
}

// ── Helpers ───────────────────────────────────────────────────────────────

// Max documents uploaded/extracted at once, so a large multi-file drop doesn't
// fire dozens of simultaneous server-side extractions.
const UPLOAD_CONCURRENCY = 4;

// Debounce for persisting survey edits as the user types.
const SURVEY_SAVE_DELAY_MS = 800;

let idCounter = 0;
const mkId = (): string => `gi-${Date.now().toString(36)}-${idCounter++}`;

const formatBytes = (bytes: number): string => {
    if (!bytes) return "0 KB";
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** A user-facing reason from an upload error (e.g. unsupported file type). */
/** Reason shown on a row we refuse before upload; matches the server's wording. */
const UNSUPPORTED_FILE_TYPE = "Unsupported file type";

const uploadErrorText = (error: any): string =>
    error?.misc || error?.message || "Couldn't add this file.";

/**
 * Distinct display types found in a file, deduped after mapping. "Other" is
 * replaced by the section's kind (capitalized) when one was extracted —
 * "Marketing" or "Email" is more useful on the pill than "Other". Mirrors the
 * server's mapping (IntakeService.distinctSectionTypes) used on resume.
 */
const sectionTypes = (sections: LogicalSection[]): string[] =>
    Array.from(
        new Set(
            sections
                .map((s) =>
                    s.type !== "Other" || !s.kind
                        ? s.type
                        : s.kind.charAt(0).toUpperCase() + s.kind.slice(1)
                )
                .filter(Boolean)
        )
    );

/** A server session message → the UI's ChatMessage shape. */
const toLocalMessage = (m: IntakeSessionMessage): ChatMessage => ({
    id: m.id,
    kind: "text",
    role: m.role,
    content: m.content,
    interjection: m.interjection,
});

/**
 * The phase a loaded/created session lands on: its chosen mode if any, otherwise
 * Upload — even with documents already in, the user still has to confirm them
 * with the Continue button to reach the mode question.
 */
const phaseForSession = (s: IntakeSession): IntakePhase =>
    s.intakeMode != null ? phaseForMode(s.intakeMode) : IntakePhase.Upload;

/** Map a server `IntakeSession` onto the local state (start and resume alike). */
const sessionToState = (s: IntakeSession): Partial<GuidedIntakeState> => ({
    projectName: s.projectName,
    projectType: s.projectType,
    analysisKind: s.analysisKind,
    documentGroups: s.documentGroups,
    intakeMode: s.intakeMode ?? null,
    phase: phaseForSession(s),
    messages: s.messages.map(toLocalMessage),
    // A persisted doc was always saved, so it's always Ready (green check + size);
    // a failed extraction just means no detected types (no pills), never an error.
    documents: s.documents.map((d) => ({
        id: `doc-${d.documentId}`,
        documentId: d.documentId,
        label: d.label,
        state: DocState.Ready,
        size: d.size != null ? formatBytes(d.size) : undefined,
        types: d.types.length > 0 ? d.types : undefined,
    })),
    topics: s.topics,
    suggestedDocumentTypes: s.suggestedDocumentTypes,
    survey: s.survey ?? null,
    uploadReadBack: s.uploadReadBack ?? null,
});

// ── Hook ──────────────────────────────────────────────────────────────────

export interface GuidedIntake {
    /** The whole intake state — render from it (and the exported selectors). */
    state: GuidedIntakeState;
    /** Send a chat turn; the agent's reply lands in the transcript. */
    sendMessage: (text: string) => void;
    /** Upload files (bounded concurrency); the agent acknowledges the batch. */
    attachFiles: (files: File[]) => void;
    /** Chat demo only: attach the scripted documents instead of real files. */
    attachDemoDocuments: () => void;
    /** Remove an uploaded document (optimistic; restored on server failure). */
    removeDocument: (doc: Document) => void;
    /** Record a name edit locally (per keystroke, no server call). */
    setProjectName: (name: string) => void;
    /** Persist the edited name (blur / Enter); no-op when unchanged. */
    commitProjectName: () => void;
    /** Type step: pick the family (Deal vs RFP analysis) — local, shows its kinds. */
    chooseType: (type: ProjectType) => void;
    /** Kind step: pick the analysis kind — mints the draft, or persists a change on revisit. */
    chooseKind: (kind: AnalysisKind) => void;
    /** Upload step: confirm the uploads and move to the mode question. */
    confirmUploads: () => void;
    /** Mode step: pick a mode — persists it and (chat) opens the conversation. */
    chooseMode: (mode: IntakeMode) => void;
    /** Record a survey edit, cached on the state and debounce-persisted. */
    updateSurvey: (survey: SurveyResponse) => void;
    /** Submit the draft for analysis (with the survey answers in survey mode).
     *  Resolves true on success; a failure is surfaced to the user and resolves false. */
    submit: () => Promise<boolean>;
    /** Step back one wizard step (no-op on the opening step). */
    back: () => void;
    /** Delete the draft and restart from the type choice (confirm first!). */
    startOver: () => Promise<void>;
}

export function useGuidedIntake(projectId?: number): GuidedIntake {
    const [state, dispatch] = useReducer(reducer, projectId, initialState);

    // The latest state, for reads that happen after an await — an async op that
    // captured `state` when it started would otherwise act on a stale snapshot
    // (e.g. miss name keystrokes typed while a request was in flight).
    const stateRef = useRef(state);
    stateRef.current = state;

    // Chat demo (Super Admin → Demos): documents and chat turns are scripted —
    // the AI backend is never called for them. The draft project itself stays
    // real (created, renamed, submitted), so the flow still ends with a project.
    const [chatDemoEnabled] = useChatDemoFlag();
    const demoRef = useRef(chatDemoEnabled);
    demoRef.current = chatDemoEnabled;
    // Which scripted exchange the next send click plays.
    const demoTurnRef = useRef(0);
    // The scripted documents attach once; further clicks are no-ops.
    const demoDocsAddedRef = useRef(false);
    // Pending script timers (fake parsing / typing), cleared on unmount and
    // start-over so a closed dialog can't receive late dispatches.
    const demoTimersRef = useRef<number[]>([]);
    const demoTimeout = (fn: () => void, ms: number) => {
        demoTimersRef.current.push(window.setTimeout(fn, ms));
    };
    useEffect(
        () => () => {
            demoTimersRef.current.forEach(clearTimeout);
        },
        []
    );

    // True while the draft is being created (from the type-choice pick), so a
    // double-click on a type card can't mint two drafts.
    const creatingRef = useRef(false);
    // The in-flight attach batch (uploads + whole-set re-read), so that picking
    // the talk-through mode waits for it before the agent's opener reads the
    // documents. Null when no batch is running.
    const pendingAckRef = useRef<Promise<void> | null>(null);
    // Batches still uploading. A settled batch only triggers the whole-set read
    // when it's the LAST one out — an earlier read would describe an incomplete
    // set (and is cancelled if a new batch starts while it's in flight).
    const activeBatchesRef = useRef(0);
    // Abort handle for the in-flight whole-set read, if any.
    const ackAbortRef = useRef<AbortController | null>(null);
    // True once the conversation has a real opening message (an agent turn;
    // legacy upload read-back interjections don't count). Monotonic.
    const conversationOpenedRef = useRef(false);
    conversationOpenedRef.current =
        conversationOpenedRef.current ||
        (state.phase === IntakePhase.Adaptive &&
            state.messages.some((m) => !m.typing && !m.interjection));

    // A fresh session shows the type-choice cards and loads nothing; only a
    // resumed draft (projectId given) fetches its session up front. Resume also
    // seeds the name bookkeeping so the existing project isn't re-renamed.
    useEffect(() => {
        if (projectId == null) return;
        let cancelled = false;
        fetchSession(projectId)
            .then((session) => {
                if (cancelled) return;
                // sessionToState resolves the phase from the persisted mode. A
                // resumed name counts as user-set, so suggestions won't overwrite it.
                dispatch({
                    type: "PATCH",
                    patch: {
                        ...sessionToState(session),
                        projectId,
                        persistedName: session.projectName ?? "",
                        nameEdited: !!session.projectName,
                    },
                });
            })
            .catch((err) => {
                if (cancelled) return;
                handleError(err);
                // Leave the spinner; fall back to the upload step so it isn't stuck.
                dispatch({ type: "PATCH", patch: { projectId, phase: IntakePhase.Upload } });
            });
        return () => {
            cancelled = true;
        };
    }, [projectId]);

    // Fetch the session and hydrate the local state from it, keeping a name the
    // user typed (edited) over the session's — then persist any keystrokes that
    // landed while the triggering call was in flight.
    const hydrateFromSession = async (projectId: number) => {
        const session = await fetchSession(projectId);
        const { nameEdited, projectName } = stateRef.current;
        const local = (projectName ?? "").trim();
        const name = nameEdited && local ? local : session.projectName ?? "";
        dispatch({
            type: "PATCH",
            patch: {
                ...sessionToState(session),
                projectId,
                projectName: name || null,
                persistedName: name,
            },
        });
        if (name && name !== (session.projectName ?? "")) {
            renameProject(projectId, name).catch(handleError);
        }
    };

    // ── Forward transitions — one op per wizard step, each owning its side
    // effects; each no-ops unless the wizard is on its step. ────────────────

    // Type step: the user picked a family (Deal vs RFP analysis). Purely local —
    // nothing exists server-side until a kind is picked; this just narrows
    // which kind cards show next.
    const chooseType = (type: ProjectType) => {
        if (state.phase !== IntakePhase.Type) return;
        dispatch({
            type: "PATCH",
            patch: { projectType: type, phase: IntakePhase.Kind },
        });
    };

    // Kind step: the user picked the analysis kind. First visit: mint the draft
    // (its type is the kind's family, carrying any name typed before the pick).
    // Revisit (via back): persist the new kind — the server resets everything
    // kind-specific with it and the project type follows the kind. Either way
    // the session is (re)loaded so the checklist and greeting match.
    // Optimistically leaves the cards for a loading state; on failure it
    // returns to the cards to retry.
    const chooseKind = (kind: AnalysisKind) => {
        if (state.phase !== IntakePhase.Kind || creatingRef.current) return;
        const { projectId, projectName, analysisKind } = state;
        if (projectId != null && kind === analysisKind) {
            // Nothing to change — back to the upload step the revisit came from.
            // A session without a mode always resumes there.
            dispatch({ type: "PATCH", patch: { phase: IntakePhase.Upload } });
            return;
        }
        creatingRef.current = true;
        dispatch({
            type: "PATCH",
            patch: { phase: IntakePhase.Loading, analysisKind: kind },
        });
        // A kind change resets the survey server-side — drop any pending
        // autosave so a stale response can't land after the reset.
        cancelPendingSurveySave();
        const applied =
            projectId == null
                ? createDraftProject(kind, (projectName ?? "").trim()).then(
                      (project) => project.id
                  )
                : setAnalysisKind(projectId, kind).then(() => projectId);
        void applied
            .then(hydrateFromSession)
            .catch((err) => {
                dispatch({
                    type: "PATCH",
                    patch: { phase: IntakePhase.Kind, analysisKind },
                });
                handleError(err);
            })
            .finally(() => {
                creatingRef.current = false;
            });
    };

    // Upload step: the user confirmed their uploads (the Continue button).
    // Purely local — nothing to persist; a resume without a chosen mode lands
    // back on Upload with Continue enabled.
    const confirmUploads = () => {
        if (state.phase !== IntakePhase.Upload) return;
        dispatch({ type: "PATCH", patch: { phase: IntakePhase.Mode } });
    };

    // Mode step: the user picked a mode. Optimistic: flip the mode locally so
    // the cards give way to the chat/survey, then persist it so a resume skips
    // the choice. On failure, revert to re-ask.
    const chooseMode = async (mode: IntakeMode) => {
        if (state.phase !== IntakePhase.Mode) return;
        const { projectId, intakeMode: previousMode } = state;
        if (projectId == null) return;
        dispatch({
            type: "PATCH",
            patch: { phase: phaseForMode(mode), intakeMode: mode },
        });
        try {
            // Persist the mode BEFORE opening the conversation: startConversation
            // rewrites the same input.content, so its (slower) turn-persist would
            // otherwise clobber a concurrent mode write and lose it.
            await setIntakeMode(projectId, mode);
        } catch (error) {
            dispatch({
                type: "PATCH",
                patch: { phase: IntakePhase.Mode, intakeMode: previousMode },
            });
            handleError(error);
            return;
        }
        // Talk it through: kick off the conversation with the agent's opening turn.
        // (Survey mode shows the form instead, no conversation.)
        if (mode === IntakeMode.CHAT) {
            void openConversation(projectId);
        }
    };

    // Step back one wizard step: Kind → Type, Upload → Kind, Mode → Upload,
    // chat/survey → Mode. Purely local — nothing is persisted (or cleared)
    // until the user re-picks something, so a resume still lands on the
    // furthest persisted step. The survey needs no special handling: its
    // answers live on the state (see updateSurvey), so the form re-seeds from
    // them when re-entered.
    const back = () => {
        switch (state.phase) {
            case IntakePhase.Kind:
                dispatch({ type: "PATCH", patch: { phase: IntakePhase.Type } });
                break;
            case IntakePhase.Upload:
                dispatch({ type: "PATCH", patch: { phase: IntakePhase.Kind } });
                break;
            case IntakePhase.Mode:
                dispatch({ type: "PATCH", patch: { phase: IntakePhase.Upload } });
                break;
            case IntakePhase.Adaptive:
            case IntakePhase.Survey:
                dispatch({ type: "PATCH", patch: { phase: IntakePhase.Mode } });
                break;
            default:
                // Type / Loading — nowhere to go back to.
                break;
        }
    };

    // Start the intake over: delete the draft server-side (documents, transcript,
    // survey and all) and return to a clean type choice. Confirmation is the
    // view's job — this just does it.
    const startOver = async () => {
        const { projectId, phase: previousPhase } = state;
        dispatch({ type: "PATCH", patch: { phase: IntakePhase.Loading } });
        try {
            if (projectId != null) await deleteDraftProject(projectId);
        } catch (error) {
            dispatch({ type: "PATCH", patch: { phase: previousPhase } });
            handleError(error);
            return;
        }
        // The draft is gone — a trailing survey autosave would only fail.
        cancelPendingSurveySave();
        conversationOpenedRef.current = false;
        pendingAckRef.current = null;
        activeBatchesRef.current = 0;
        ackAbortRef.current?.abort();
        ackAbortRef.current = null;
        demoTimersRef.current.forEach(clearTimeout);
        demoTimersRef.current = [];
        demoTurnRef.current = 0;
        demoDocsAddedRef.current = false;
        dispatch({ type: "PATCH", patch: initialState() });
    };

    // ── Survey persistence ────────────────────────────────────────────────
    // The form reports every edit through updateSurvey; the state copy is the
    // source of truth for remounts and submit, and the server copy follows on a
    // debounce. The pending save lives in refs so the unmount flush (dialog
    // close) sees the latest values, and so it can be cancelled when the draft
    // stops existing or stops being a draft (start over, type change, submit).
    const surveySaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingSurveyRef = useRef<SurveyResponse | null>(null);

    const cancelPendingSurveySave = () => {
        if (surveySaveTimerRef.current) clearTimeout(surveySaveTimerRef.current);
        surveySaveTimerRef.current = null;
        pendingSurveyRef.current = null;
    };

    const persistPendingSurvey = () => {
        const survey = pendingSurveyRef.current;
        const { projectId } = stateRef.current;
        cancelPendingSurveySave();
        if (survey && projectId != null) {
            saveIntakeSurvey(projectId, survey)
                // Answers can cover topics — refresh the Context checklist.
                // (Guarded: a stale server during a deploy may omit topics.)
                .then((res) => {
                    if (res.topics) dispatch({ type: "PATCH", patch: { topics: res.topics } });
                })
                .catch(handleError);
        }
    };

    const updateSurvey = (survey: SurveyResponse) => {
        dispatch({ type: "PATCH", patch: { survey } });
        pendingSurveyRef.current = survey;
        if (surveySaveTimerRef.current) clearTimeout(surveySaveTimerRef.current);
        surveySaveTimerRef.current = setTimeout(persistPendingSurvey, SURVEY_SAVE_DELAY_MS);
    };

    // Flush any pending survey edit when the dialog closes (unmount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => persistPendingSurvey(), []);

    // Adopt a suggested name (from an extraction or the agent) only when the
    // project has no name yet — never override one the user set, or one an earlier
    // extraction already proposed. Reads through stateRef: suggestions arrive from
    // async turns, and a name typed meanwhile must win.
    const suggestName = (suggested?: string) => {
        if (!suggested) return;
        const { nameEdited, projectName, projectId, persistedName } = stateRef.current;
        if (nameEdited || (projectName ?? "").trim()) return;
        dispatch({
            type: "PATCH",
            patch: { projectName: suggested, persistedName: suggested },
        });
        if (projectId != null && suggested !== persistedName) {
            renameProject(projectId, suggested).catch(handleError);
        }
    };

    // Open the adaptive conversation: fetch the transcript plus the opening
    // question — grounded in the documents when there are any, else the first
    // uncovered topic's canned question — posted server-side.
    const openConversation = async (projectId: number) => {
        // Already open (a real agent turn is rendered — interjections don't
        // count): re-entering the chat has nothing to fetch. (Read through
        // stateRef: this runs after the mode-persist await.)
        if (stateRef.current.messages.some((m) => !m.typing && !m.interjection)) return;
        const typingId = mkId();
        dispatch({
            type: "ADD_MESSAGE",
            message: { id: typingId, kind: "text", role: "system", content: "", typing: true },
        });
        dispatch({ type: "PATCH", patch: { sending: true } });
        // Chat demo: the opener is scripted, not fetched.
        if (demoRef.current) {
            demoTimeout(() => {
                dispatch({
                    type: "REPLACE_MESSAGE",
                    id: typingId,
                    message: {
                        id: typingId,
                        kind: "text",
                        role: "system",
                        content: DEMO_CONVERSATION_OPENER,
                    },
                });
                dispatch({ type: "PATCH", patch: { sending: false } });
            }, 1400);
            return;
        }
        try {
            // If an attach batch is still in flight, wait for it so the agent's
            // opener sees every uploaded document.
            const pending = pendingAckRef.current;
            if (pending) await pending;
            const res = await startConversation(projectId);
            // Replace the transcript wholesale (dropping the typing placeholder):
            // the server's copy is authoritative and carries every read-back, not
            // just the last batch's.
            dispatch({
                type: "PATCH",
                patch: { messages: res.messages.map(toLocalMessage), topics: res.topics },
            });
        } catch (error) {
            dispatch({
                type: "REPLACE_MESSAGE",
                id: typingId,
                message: {
                    id: typingId,
                    kind: "text",
                    role: "system",
                    content: "Sorry — something went wrong. Please try again later.",
                },
            });
            handleError(error);
        } finally {
            dispatch({ type: "PATCH", patch: { sending: false } });
        }
    };

    // Chat demo: mark context topics covered as scripted turns play — the next
    // pending one per turn, or every remaining one on the final turn. The
    // topics are the kind's real checklist, so this works for any deal kind.
    const advanceDemoTopics = (rest: boolean) => {
        let marked = false;
        const topics = stateRef.current.topics.map((t) => {
            if (t.status !== "pending" || (marked && !rest)) return t;
            marked = true;
            return { ...t, status: "done" as const };
        });
        dispatch({ type: "PATCH", patch: { topics } });
    };

    const sendMessage = async (text: string) => {
        // Chat demo: a send click plays the next scripted exchange — the typed
        // text (usually none; the composer is clickable-empty) is ignored.
        if (demoRef.current) {
            const turn = DEMO_EXCHANGES[demoTurnRef.current];
            if (!turn || stateRef.current.sending) return;
            demoTurnRef.current += 1;
            dispatch({
                type: "ADD_MESSAGE",
                message: { id: mkId(), kind: "text", role: "user", content: turn.user },
            });
            const typingId = mkId();
            dispatch({
                type: "ADD_MESSAGE",
                message: { id: typingId, kind: "text", role: "system", content: "", typing: true },
            });
            dispatch({ type: "PATCH", patch: { sending: true } });
            demoTimeout(() => {
                dispatch({
                    type: "REPLACE_MESSAGE",
                    id: typingId,
                    message: { id: typingId, kind: "text", role: "system", content: turn.reply },
                });
                advanceDemoTopics(demoTurnRef.current >= DEMO_EXCHANGES.length);
                dispatch({ type: "PATCH", patch: { sending: false } });
            }, turn.replyMs);
            return;
        }

        const trimmed = text.trim();
        if (!trimmed) return;

        dispatch({
            type: "ADD_MESSAGE",
            message: { id: mkId(), kind: "text", role: "user", content: trimmed },
        });
        const typingId = mkId();
        dispatch({
            type: "ADD_MESSAGE",
            message: { id: typingId, kind: "text", role: "system", content: "", typing: true },
        });
        dispatch({ type: "PATCH", patch: { sending: true } });

        try {
            const { projectId } = stateRef.current;
            if (projectId == null) throw new Error("No project — pick a type first");
            const res = await postMessage(projectId, trimmed);
            dispatch({
                type: "REPLACE_MESSAGE",
                id: typingId,
                message: { id: typingId, kind: "text", role: "system", content: res.message.text },
            });
            dispatch({ type: "PATCH", patch: { topics: res.topics } });
            suggestName(res.suggestedName);
        } catch (error) {
            dispatch({
                type: "REPLACE_MESSAGE",
                id: typingId,
                message: {
                    id: typingId,
                    kind: "text",
                    role: "system",
                    content: "Sorry — something went wrong. Please try again later.",
                },
            });
            handleError(error);
        } finally {
            dispatch({ type: "PATCH", patch: { sending: false } });
        }
    };

    // Upload one queued file: mark it parsing, store + extract, then ready/failed.
    // Returns the document id on success, null on failure.
    const uploadOne = async (
        file: File,
        docId: string
    ): Promise<{ documentId: number } | null> => {
        dispatch({ type: "SET_DOC", id: docId, patch: { state: DocState.Parsing } });
        try {
            const { projectId } = stateRef.current;
            if (projectId == null) throw new Error("No project — pick a type first");
            const res = await uploadDocument(projectId, file);
            const extracted = res.extractionStatus !== ExtractionStatus.FAILED;
            dispatch({
                type: "SET_DOC",
                id: docId,
                patch: {
                    state: DocState.Ready,
                    documentId: res.documentId,
                    size: formatBytes(res.size),
                    types: extracted ? sectionTypes(res.sections) : undefined,
                },
            });
            // The document may cover topics (e.g. a quote's TCV) — refresh the
            // checklist right away, in every mode. (Guarded: a stale server
            // during a deploy may omit topics.)
            if (res.topics) dispatch({ type: "PATCH", patch: { topics: res.topics } });
            // Name isn't set from individual uploads — the batch acknowledgement does it.
            return { documentId: res.documentId };
        } catch (error) {
            // Rejected (e.g. unsupported type) — keep the row and show the reason
            // on it, rather than a popup error.
            dispatch({
                type: "SET_DOC",
                id: docId,
                patch: { state: DocState.Failed, error: uploadErrorText(error) },
            });
            return null;
        }
    };

    // Re-read the WHOLE document set after an upload batch settles: refresh
    // the read-back shown on the upload step (a later batch replaces it), the
    // checklist, and the suggested name. Nothing is ever shown in the chat —
    // mid-conversation uploads are silent (the documents show in the panel and
    // the agent sees them on its next turn), so we skip the read entirely.
    const acknowledgeBatch = async (): Promise<void> => {
        const { projectId, phase } = stateRef.current;
        if (projectId == null || phase === IntakePhase.Adaptive) return;
        // Another batch is still uploading — skip; its settle re-reads the
        // whole set (including this batch's documents).
        if (activeBatchesRef.current > 0) return;
        const controller = new AbortController();
        ackAbortRef.current?.abort();
        ackAbortRef.current = controller;
        dispatch({ type: "PATCH", patch: { ackPending: true } });
        try {
            const res = await acknowledgeUploads(projectId, { signal: controller.signal });
            if (controller.signal.aborted) return; // superseded by a newer batch
            suggestName(res.suggestedName);
            dispatch({
                type: "PATCH",
                patch: { topics: res.topics, uploadReadBack: res.message.text || null },
            });
        } catch (error) {
            if (!controller.signal.aborted) handleError(error);
        } finally {
            if (ackAbortRef.current === controller) ackAbortRef.current = null;
            dispatch({ type: "PATCH", patch: { ackPending: false } });
        }
    };

    // Attach one or more files (multi-select or drag-and-drop). The uploads run
    // through a bounded-concurrency queue (so a 20-file drop doesn't fire 20
    // simultaneous extractions); they all target the draft created at type-choice.
    // Once they settle we acknowledge the batch.
    const attachFiles = (files: File[]) => {
        if (!files.length) return;

        // The picker's `accept` list doesn't constrain drag-and-drop, so reject
        // unsupported types here. The row shows the reason the server would
        // have given, without the round trip.
        const accepted: File[] = [];
        for (const file of files) {
            if (hasAllowedExtension(file.name)) {
                accepted.push(file);
                continue;
            }
            dispatch({
                type: "ADD_DOC",
                doc: {
                    id: mkId(),
                    label: file.name,
                    state: DocState.Failed,
                    error: UNSUPPORTED_FILE_TYPE,
                },
            });
        }
        if (!accepted.length) return;
        const total = accepted.length;

        activeBatchesRef.current += 1;
        // A new batch makes any in-flight whole-set read stale — cancel it;
        // this batch re-reads everything when it settles.
        ackAbortRef.current?.abort();

        const items = accepted.map((file) => ({ file, docId: mkId() }));
        items.forEach(({ file, docId }) =>
            dispatch({ type: "ADD_DOC", doc: { id: docId, label: file.name, state: DocState.Queued } })
        );

        // Progress is shown by the Documents title (not as a chat message).
        dispatch({ type: "PATCH", patch: { uploadProgress: { done: 0, total } } });

        let uploaded = 0;
        let done = 0;
        let cursor = 0;
        const worker = async () => {
            while (cursor < items.length) {
                const { file, docId } = items[cursor++];
                const result = await uploadOne(file, docId);
                if (result) uploaded += 1;
                done += 1;
                dispatch({ type: "PATCH", patch: { uploadProgress: { done, total } } });
            }
        };
        void Promise.all(
            Array.from({ length: Math.min(UPLOAD_CONCURRENCY, total) }, worker)
        ).then(() => {
            activeBatchesRef.current -= 1;
            dispatch({ type: "PATCH", patch: { uploadProgress: null } });
            if (uploaded === 0) return;
            // Publish the whole-set re-read so a talk-through pick can wait for
            // it (the opener should see every document uploaded).
            const ack = acknowledgeBatch();
            pendingAckRef.current = ack;
            void ack.finally(() => {
                if (pendingAckRef.current === ack) pendingAckRef.current = null;
            });
        });
    };

    // Chat demo: attach the scripted documents — rows appear parsing, turn
    // Ready on the script's pacing, then the scripted whole-set read-back and
    // name suggestion land. Nothing is uploaded; the rows carry negative
    // documentIds so they count as landed but are never sent to the server.
    // One batch per session: further clicks (and re-clicks mid-parse) no-op.
    const attachDemoDocuments = () => {
        if (demoDocsAddedRef.current) return;
        demoDocsAddedRef.current = true;
        const { documentGroups } = stateRef.current;
        const total = DEMO_DOCUMENTS.length;
        const items = DEMO_DOCUMENTS.map((doc, i) => ({
            doc,
            docId: mkId(),
            // Pill types come from the kind's real requirement groups when it
            // has them, so the checklist's document rows read as satisfied on
            // any kind; the script's types are the fallback.
            types: documentGroups[i]?.documentTypes.length
                ? documentGroups[i].documentTypes
                : doc.types,
        }));
        items.forEach(({ doc, docId }) =>
            dispatch({
                type: "ADD_DOC",
                doc: { id: docId, label: doc.filename, state: DocState.Parsing },
            })
        );
        dispatch({ type: "PATCH", patch: { uploadProgress: { done: 0, total } } });
        let done = 0;
        items.forEach(({ doc, docId, types }, i) => {
            demoTimeout(() => {
                done += 1;
                dispatch({
                    type: "SET_DOC",
                    id: docId,
                    patch: {
                        state: DocState.Ready,
                        documentId: -(i + 1),
                        size: doc.size,
                        types,
                    },
                });
                dispatch({
                    type: "PATCH",
                    patch: { uploadProgress: done < total ? { done, total } : null },
                });
            }, doc.parseMs);
        });
        const settledMs = Math.max(...DEMO_DOCUMENTS.map((d) => d.parseMs));
        demoTimeout(() => dispatch({ type: "PATCH", patch: { ackPending: true } }), settledMs);
        demoTimeout(() => {
            suggestName(DEMO_SUGGESTED_NAME);
            dispatch({
                type: "PATCH",
                patch: { ackPending: false, uploadReadBack: DEMO_UPLOAD_READ_BACK },
            });
        }, settledMs + DEMO_ACK_MS);
    };

    // Remove an uploaded document. Optimistic: drop it from the panel right away,
    // tell the server when it has a persisted id, and restore the row on failure.
    // Scripted demo documents (negative ids) exist only locally — nothing to tell.
    const removeDocument = async (doc: Document) => {
        dispatch({ type: "REMOVE_DOC", id: doc.id });
        const { projectId } = stateRef.current;
        if (doc.documentId == null || doc.documentId < 0 || projectId == null) return;
        try {
            await deleteDocument(projectId, doc.documentId);
        } catch (error) {
            dispatch({ type: "ADD_DOC", doc });
            handleError(error);
        }
    };

    // The user is editing the name field — a local edit on every keystroke, no
    // server call (the rename happens on commitProjectName). Editing marks the
    // name user-set, so suggestions stop overriding it.
    const setProjectName = (name: string) => {
        dispatch({ type: "PATCH", patch: { projectName: name, nameEdited: true } });
    };

    // Persist the edited name (called on blur / Enter). No-ops until the project
    // exists, and deduped against the last persisted value so a
    // blur-without-change doesn't spam the rename endpoint.
    const commitProjectName = () => {
        const { projectId, projectName, persistedName } = stateRef.current;
        const name = (projectName ?? "").trim();
        if (projectId == null || !name || name === persistedName) return;
        dispatch({ type: "PATCH", patch: { persistedName: name } });
        renameProject(projectId, name).catch(handleError);
    };

    // In survey mode the completed form (schema + answers, kept current by
    // updateSurvey) is submitted with the draft; in adaptive mode the transcript
    // is the input and no survey is sent.
    const submit = async (): Promise<boolean> => {
        const { projectId } = stateRef.current;
        if (projectId == null) return false;
        const survey =
            state.phase === IntakePhase.Survey ? state.survey ?? undefined : undefined;
        dispatch({ type: "PATCH", patch: { submitting: true } });
        try {
            await submitProject(projectId, survey);
            // The draft just left DRAFT status — a trailing autosave (debounced or
            // the unmount flush when the dialog closes) would only fail.
            cancelPendingSurveySave();
            return true;
        } catch (error) {
            // Still a draft — the dialog stays open and the user can retry.
            handleError(error);
            return false;
        } finally {
            dispatch({ type: "PATCH", patch: { submitting: false } });
        }
    };

    return {
        state,
        sendMessage,
        attachFiles,
        attachDemoDocuments,
        removeDocument,
        setProjectName,
        commitProjectName,
        chooseType,
        chooseKind,
        confirmUploads,
        chooseMode,
        back,
        startOver,
        updateSurvey,
        submit,
    };
}
